import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PostCard } from '@/components/home/post-card'
import { SchoolSelector } from '@/components/home/school-selector'
import { SkeletonList } from '@/components/ui/skeleton-card'
import { useContentStore } from '@/stores/content-store'
import { useAuthStore } from '@/stores/auth-store'
import { PageTransition, EmptyState } from '@/components/ui/ux-enhancer'
import { useToast } from '@/components/ui/toast-provider'
import { Button } from '@/components/ui/button'
import { 
  Heart, 
  MessageSquare, 
  Search, 
  Filter,
  Grid3X3,
  List,
  SortAsc,
  SortDesc,
  Clock,
  TrendingUp
} from 'lucide-react'

// 分类图标映射（与后端分类枚举一致）
const categoryIcons: { [key: string]: any } = {
  '学习': Grid3X3,
  '生活': Heart,
  '娱乐': TrendingUp,
  '求助': MessageSquare,
  '交易': Grid3X3,
  '其他': List
}

// 排序选项
const sortOptions = [
  { id: 'latest', name: '最新发布', icon: Clock },
  { id: 'popular', name: '最受欢迎', icon: TrendingUp },
  { id: 'likes', name: '点赞最多', icon: Heart },
  { id: 'comments', name: '评论最多', icon: MessageSquare }
]

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>()
  const [selectedSchool, setSelectedSchool] = useState('全部学校')
  const [sortBy, setSortBy] = useState('latest')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const { posts, fetchPosts, isLoading } = useContentStore()
  const { user } = useAuthStore()
  const { addToast } = useToast()
  const navigate = useNavigate()

  // 获取当前分类的中文名称
  const currentCategory = category ? decodeURIComponent(category) : '全部'
  const CategoryIcon = categoryIcons[currentCategory] || List

  useEffect(() => {
    // 按分类从后端拉取帖子（'全部' 时不传分类）
    fetchPosts({ category: currentCategory })
  }, [fetchPosts, currentCategory])

  // 时间格式化函数
  function formatTime(dateString: string): string {
    const now = new Date()
    const postTime = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '刚刚'
    if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}小时前`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}天前`
    
    return postTime.toLocaleDateString('zh-CN')
  }

  // 筛选和排序帖子（分类已由后端过滤，本地只做学校筛选与排序）
  const filteredAndSortedPosts = posts
    .filter(post => {
      // 学校筛选
      const schoolMatch = selectedSchool === '全部学校' || post.school === selectedSchool
      
      return schoolMatch
    })
    .map(post => ({
      id: post.id,
      userId: post.userId,
      author: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      user: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      content: post.content,
      title: post.title,
      images: post.images || [],
      category: post.category,
      createdAt: new Date(post.createdAt),
      time: formatTime(post.createdAt),
      likes: post.likes || 0,
      comments: post.comments || 0,
      location: post.location,
      price: post.price,
      condition: post.condition,
      tags: post.tags || []
    }))
    .sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        case 'popular':
          return (b.likes + b.comments) - (a.likes + a.comments)
        case 'likes':
          return b.likes - a.likes
        case 'comments':
          return b.comments - a.comments
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }
    })

  if (isLoading) {
    return (
      <PageTransition>
        <div className="space-y-4 lg:space-y-6">
          {/* 页面标题骨架屏 */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-32 h-6 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="w-48 h-4 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* 控制栏骨架屏 */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex gap-4">
              <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
              <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
              <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>

          <SkeletonList count={5} />
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition>
      <div className="space-y-4 lg:space-y-6">
        {/* 页面标题 */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CategoryIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{currentCategory}</h1>
              <p className="text-gray-600">
                {currentCategory === '全部' ? '浏览所有分类的内容' : `浏览${currentCategory}相关内容`}
              </p>
            </div>
          </div>
          
          {/* 统计信息 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{filteredAndSortedPosts.length}</div>
              <div className="text-sm text-gray-600">内容数量</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">
                {filteredAndSortedPosts.reduce((sum, post) => sum + post.likes, 0)}
              </div>
              <div className="text-sm text-gray-600">总点赞</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">
                {filteredAndSortedPosts.reduce((sum, post) => sum + post.comments, 0)}
              </div>
              <div className="text-sm text-gray-600">总评论</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-500">
                {new Set(filteredAndSortedPosts.map(post => post.author.school)).size}
              </div>
              <div className="text-sm text-gray-600">参与学校</div>
            </div>
          </div>
        </div>

        {/* 筛选和排序控制栏 */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* 左侧控制 */}
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* 学校选择器 */}
              <div className="min-w-0 flex-1">
                <SchoolSelector 
                  selectedSchool={selectedSchool}
                  onSchoolChange={setSelectedSchool}
                />
              </div>
              
              {/* 排序选择 */}
              <div className="flex gap-2 overflow-x-auto">
                {sortOptions.map((option) => {
                  const Icon = option.icon
                  return (
                    <Button
                      key={option.id}
                      variant={sortBy === option.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSortBy(option.id)}
                      className="flex-shrink-0"
                    >
                      <Icon className="w-4 h-4 mr-1" />
                      {option.name}
                    </Button>
                  )
                })}
              </div>
            </div>

            {/* 右侧视图切换 */}
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'list' ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* 内容区域 */}
        <div className={`space-y-4 lg:space-y-6 ${viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6' : ''}`}>
          {filteredAndSortedPosts.length > 0 ? (
            filteredAndSortedPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))
          ) : (
            <EmptyState
              icon={<CategoryIcon className="w-16 h-16 text-gray-300" />}
              title={`暂无${currentCategory}内容`}
              description="当前筛选条件下没有找到相关内容，试试其他条件或发布第一条内容吧"
              action={
                <button
                  onClick={() => {
                    navigate('/publish')
                    addToast({
                      type: 'info',
                      title: '跳转发布页',
                      description: '开始创建你的第一条内容'
                    })
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  发布内容
                </button>
              }
            />
          )}
        </div>
      </div>
    </PageTransition>
  )
}